import type { FC, ReactNode } from "react";
import { Switch as LibSwitch } from "@headlessui/react";
import { cx } from "../../../utils/classnames";

export enum LabelPosition {
  Left = "left",
  Right = "right"
}

export enum SwitchSize {
  SM = "SM",
  BASE = "BASE"
}

export interface SwitchProps {
  checked: boolean;
  label?: ReactNode;
  onChange: (v: boolean) => void;
  className?: string;
  disabled?: boolean;
  wrapperClassName?: string;
  switchClassName?: string;
  position?: LabelPosition;
  size?: SwitchSize;
}

const switchSize = {
  [SwitchSize.SM]: {
    switch: "h-5 w-9",
    thumb: "size-4",
    thumbPosition: {
      checked: "translate-x-[18px]",
      unchecked: "translate-x-[2px]"
    }
  },
  [SwitchSize.BASE]: {
    switch: "h-6 w-11",
    thumb: "size-4",
    thumbPosition: {
      checked: "translate-x-6",
      unchecked: "translate-x-1"
    }
  }
};

const LabelWrapper: FC<{
  label?: ReactNode;
  position?: LabelPosition;
  children: ReactNode;
}> = ({ label, position = LabelPosition.Left, children }) => {
  if (!label) return <>{children}</>;

  return (
    <>
      {position === LabelPosition.Left ? <>{label}</> : null}
      {children}
      {position === LabelPosition.Right ? <>{label}</> : null}
    </>
  );
};

export const Switch: FC<SwitchProps> = ({
  checked,
  label,
  onChange,
  className,
  disabled,
  wrapperClassName,
  switchClassName,
  position = LabelPosition.Right,
  size = SwitchSize.BASE
}) => {
  return (
    <LibSwitch.Group as="div" className="space-y-4">
      <div className={cx("flex items-center gap-3", wrapperClassName)}>
        <LabelWrapper
          label={
            label ? (
              <LibSwitch.Description
                as="p"
                className={className ?? "text-sm font-medium text-content-1000"}
              >
                {label}
              </LibSwitch.Description>
            ) : null
          }
          position={position}
        >
          <LibSwitch
            checked={checked}
            className={cx(
              checked ? "bg-primary-500" : "bg-alpha-100",
              "shrink-0 relative inline-flex items-center rounded-full transition-colors focus:outline-none",
              switchSize[size].switch,
              switchClassName
            )}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              onChange(!checked);
            }}
          >
            <span
              aria-hidden="true"
              className={cx(
                checked
                  ? switchSize[size].thumbPosition.checked
                  : switchSize[size].thumbPosition.unchecked,
                "inline-block shadow-sm transform rounded-full bg-white transition-transform",
                switchSize[size].thumb
              )}
            />
          </LibSwitch>
        </LabelWrapper>
      </div>
    </LibSwitch.Group>
  );
};
